import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/Layout';
import { API_ENDPOINTS } from '../../config/api';
import '../../config/axiosConfig';

const DATE_FILTERS = [
  { id: 'all', label: 'All time' },
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'week', label: 'Week' },
  { id: '15days', label: '15 days' },
  { id: 'month', label: 'Month' },
  { id: 'custom', label: 'Custom' },
];

const todayKey = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Kolkata',
  });
}

function MiniDailyChart({ series }) {
  if (!series?.length) return <span className="text-xs text-stone-400">No activity</span>;
  const max = Math.max(...series.map((d) => d.count), 1);
  return (
    <div className="flex items-end gap-0.5 h-8" title="Daily onboardings in selected period">
      {series.map((d) => (
        <div
          key={d.date}
          className="w-1.5 bg-indigo-400 rounded-t min-h-[2px]"
          style={{ height: `${Math.max(4, (d.count / max) * 32)}px` }}
          title={`${d.date}: ${d.count}`}
        />
      ))}
    </div>
  );
}

export default function EmployeePerformancePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateFilter, setDateFilter] = useState('month');
  const [customFrom, setCustomFrom] = useState(todayKey());
  const [customTo, setCustomTo] = useState(todayKey());
  const [expandedId, setExpandedId] = useState(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const from = dateFilter === 'custom' ? customFrom : undefined;
      const to = dateFilter === 'custom' ? customTo : undefined;
      const res = await axios.get(API_ENDPOINTS.COMPANY_EMPLOYEE_PERFORMANCE(dateFilter, from, to));
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load performance');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dateFilter, customFrom, customTo]);

  useEffect(() => {
    if (dateFilter === 'custom') return;
    load();
  }, [load, dateFilter]);

  const applyCustomRange = (e) => {
    e.preventDefault();
    if (!customFrom || !customTo) {
      setError('Select both start and end dates');
      return;
    }
    if (customFrom > customTo) {
      setError('Start date cannot be after end date');
      return;
    }
    load();
  };

  const filteredEmployees = (data?.employees || []).filter((row) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      row.name?.toLowerCase().includes(q) ||
      row.empId?.toLowerCase().includes(q) ||
      row.email?.toLowerCase().includes(q)
    );
  });

  const summary = data?.summary;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <Link
              to="/superadmin"
              className="text-indigo-600 hover:text-indigo-800 text-sm font-medium mb-2 inline-block"
            >
              ← Back to Dashboard
            </Link>
            <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wide">
              Team analytics
            </p>
            <h1 className="text-2xl font-bold text-stone-900">Employee performance</h1>
            <p className="text-sm text-stone-500 mt-1">
              PocketBillingAI shop onboarding by company employees — daily work and totals.
            </p>
          </div>
          <Link
            to="/superadmin/employees"
            className="text-sm text-indigo-600 font-medium hover:underline shrink-0"
          >
            Manage employees →
          </Link>
        </div>

        <div className="bg-white rounded-xl border border-stone-200 p-4 shadow-sm space-y-3">
          <p className="text-sm font-semibold text-stone-800">Report period</p>
          <div className="flex flex-wrap gap-2">
            {DATE_FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setDateFilter(f.id)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${
                  dateFilter === f.id
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-stone-700 border-stone-300 hover:bg-stone-50'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          {dateFilter === 'custom' && (
            <form
              onSubmit={applyCustomRange}
              className="flex flex-col sm:flex-row flex-wrap items-end gap-3"
            >
              <label className="text-sm text-stone-600">
                From
                <input
                  type="date"
                  required
                  className="mt-1 block border rounded-lg px-3 py-2"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                />
              </label>
              <label className="text-sm text-stone-600">
                To
                <input
                  type="date"
                  required
                  className="mt-1 block border rounded-lg px-3 py-2"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                />
              </label>
              <button
                type="submit"
                className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-medium"
              >
                Apply
              </button>
            </form>
          )}
          {data?.dateRange?.label && (
            <p className="text-xs text-stone-500">
              Period: <strong>{data.dateRange.label}</strong>
            </p>
          )}
        </div>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </p>
        )}

        {loading ? (
          <p className="text-stone-500">Loading performance…</p>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl border border-stone-200 p-4 shadow-sm">
                <p className="text-xs text-stone-500 uppercase font-semibold">Shops in period</p>
                <p className="text-2xl font-bold text-indigo-700 mt-1">
                  {summary?.shopsInPeriod ?? 0}
                </p>
              </div>
              <div className="bg-white rounded-xl border border-stone-200 p-4 shadow-sm">
                <p className="text-xs text-stone-500 uppercase font-semibold">All-time onboarded</p>
                <p className="text-2xl font-bold text-stone-800 mt-1">
                  {summary?.shopsAllTime ?? 0}
                </p>
              </div>
              <div className="bg-white rounded-xl border border-stone-200 p-4 shadow-sm">
                <p className="text-xs text-stone-500 uppercase font-semibold">Active employees</p>
                <p className="text-2xl font-bold text-stone-800 mt-1">
                  {summary?.activeEmployees ?? 0}
                  <span className="text-sm font-normal text-stone-500">
                    {' '}
                    / {summary?.totalEmployees ?? 0}
                  </span>
                </p>
              </div>
              <div className="bg-white rounded-xl border border-stone-200 p-4 shadow-sm">
                <p className="text-xs text-stone-500 uppercase font-semibold">Top in period</p>
                <p className="text-lg font-bold text-emerald-700 mt-1 truncate">
                  {summary?.topPerformer?.name || '—'}
                </p>
                {summary?.topPerformer && (
                  <p className="text-xs text-stone-500 font-mono">
                    {summary.topPerformer.empId} · {summary.topPerformer.shopsInPeriod} shops
                  </p>
                )}
              </div>
            </div>

            {summary?.unassignedShops > 0 && (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                {summary.unassignedShops} shop(s) have no employee linked (onboarded before tracking).
              </p>
            )}

            <input
              type="search"
              placeholder="Search employee name, ID, email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full max-w-md border border-stone-200 rounded-lg px-3 py-2 text-sm"
            />

            <div className="bg-white rounded-xl border border-stone-200 overflow-x-auto shadow-sm">
              <table className="w-full text-sm min-w-[1000px]">
                <thead className="bg-stone-50 text-stone-600">
                  <tr>
                    <th className="text-left px-4 py-3">Employee</th>
                    <th className="text-right px-4 py-3">Period</th>
                    <th className="text-right px-4 py-3">Today</th>
                    <th className="text-right px-4 py-3">This week</th>
                    <th className="text-right px-4 py-3">This month</th>
                    <th className="text-right px-4 py-3">All time</th>
                    <th className="text-left px-4 py-3">Last onboarded</th>
                    <th className="text-left px-4 py-3">Daily trend</th>
                    <th className="text-right px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {filteredEmployees.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-4 py-8 text-center text-stone-500">
                        No employees match your search.
                      </td>
                    </tr>
                  ) : (
                    filteredEmployees.map((row) => (
                      <React.Fragment key={row.id}>
                        <tr className="hover:bg-stone-50/80">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              {row.photoUrl ? (
                                <img
                                  src={row.photoUrl}
                                  alt=""
                                  className="w-9 h-9 rounded-full object-cover border"
                                />
                              ) : (
                                <div className="w-9 h-9 rounded-full bg-stone-100 flex items-center justify-center text-stone-400 text-xs">
                                  —
                                </div>
                              )}
                              <div>
                                <span className="font-medium block">{row.name}</span>
                                <span className="text-xs font-mono text-stone-500">{row.empId}</span>
                                {!row.isActive && (
                                  <span className="text-xs text-stone-400 block">Inactive</span>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right font-bold text-indigo-700 tabular-nums">
                            {row.stats.inPeriod}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums">{row.stats.today}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{row.stats.thisWeek}</td>
                          <td className="px-4 py-3 text-right tabular-nums">{row.stats.thisMonth}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-stone-600">
                            {row.stats.allTime}
                          </td>
                          <td className="px-4 py-3 text-stone-600 whitespace-nowrap">
                            {formatDate(row.stats.lastOnboardedAt)}
                          </td>
                          <td className="px-4 py-3">
                            <MiniDailyChart series={row.dailySeries} />
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              type="button"
                              className="text-xs text-indigo-600 font-medium"
                              onClick={() =>
                                setExpandedId(expandedId === row.id ? null : row.id)
                              }
                            >
                              {expandedId === row.id ? 'Hide' : 'Details'}
                            </button>
                          </td>
                        </tr>
                        {expandedId === row.id && (
                          <tr className="bg-indigo-50/30">
                            <td colSpan={9} className="px-4 py-4">
                              <p className="text-xs font-semibold text-stone-600 mb-2">
                                Recent shops onboarded
                              </p>
                              {row.recentShops?.length ? (
                                <ul className="grid sm:grid-cols-2 gap-2 text-sm">
                                  {row.recentShops.map((s, i) => (
                                    <li
                                      key={i}
                                      className="flex justify-between gap-2 bg-white rounded-lg border border-stone-100 px-3 py-2"
                                    >
                                      <span>
                                        <strong>{s.shopName}</strong>
                                        <span className="block text-stone-500 text-xs">
                                          {s.ownerName}
                                        </span>
                                      </span>
                                      <span className="text-xs text-stone-500 shrink-0">
                                        {formatDate(s.onboardedAt)}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="text-sm text-stone-500">No shops yet.</p>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
